import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { MdTrendingUp, MdEdit, MdCheck, MdClose } from "react-icons/md";

const API = process.env.REACT_APP_API_URL || '';

// Profile color options (6 variations)
const PROFILE_COLORS = [
  { name: "Purple", value: "#667eea" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Green", value: "#10b981" },
  { name: "Pink", value: "#ec4899" },
  { name: "Orange", value: "#f97316" },
  { name: "Teal", value: "#14b8a6" }
];

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export default function ProfilePage({ user: userProp }) {
  const [user, setUser] = useState(userProp);
  const [loading, setLoading] = useState(true);
  const [editingColor, setEditingColor] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [updatingColor, setUpdatingColor] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const fetchUserProfile = useCallback(async () => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    try {
      setLoading(true);
      // Add timestamp to prevent browser caching
      const res = await axios.get(`${API}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: Date.now() }
      });
      setUser(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    // Always fetch fresh data on mount, when token changes, or when navigating to this page
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname]);

  // Refetch when page becomes visible (user navigates back to profile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && token) {
        fetchUserProfile();
      }
    };

    const handleFocus = () => {
      if (token) {
        fetchUserProfile();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [token, fetchUserProfile]);

  // Also refetch if userProp changes (when App.js updates user after voting)
  useEffect(() => {
    if (userProp && userProp.id && user && user.id === userProp.id) {
      // Check if userProp has newer data (different points or streak)
      if (userProp.pointsLeftToday !== user.pointsLeftToday || 
          userProp.currentStreak !== user.currentStreak ||
          userProp.totalVotes !== user.totalVotes ||
          userProp.profileColor !== user.profileColor) {
        fetchUserProfile();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProp?.pointsLeftToday, userProp?.currentStreak, userProp?.totalVotes, userProp?.profileColor]);

  // Initialize selected color when user loads
  useEffect(() => {
    if (user?.profileColor) {
      setSelectedColor(user.profileColor);
    }
  }, [user?.profileColor]);

  const handleColorUpdate = async (color) => {
    if (!token || updatingColor) return;
    
    try {
      setUpdatingColor(true);
      const res = await axios.put(
        `${API}/api/user/update-profile-color`,
        { profileColor: color },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setUser(prev => ({ ...prev, profileColor: res.data.profileColor }));
      setSelectedColor(res.data.profileColor);
      setEditingColor(false);
      
      // Update parent component if needed
      if (userProp && window.updateUser) {
        window.updateUser({ ...user, profileColor: res.data.profileColor });
      }
    } catch (err) {
      alert("Failed to update profile color. Please try again.");
    } finally {
      setUpdatingColor(false);
    }
  };

  const handleEditColor = () => {
    setSelectedColor(user?.profileColor || "#667eea");
    setEditingColor(true);
  };

  const handleCancelEdit = () => {
    setSelectedColor(user?.profileColor || "#667eea");
    setEditingColor(false);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-indicator">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="loading-indicator">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <h2>Profile</h2>

      <div className="profile-section">
        <div className="profile-header">
          <div className="profile-avatar-wrapper">
            <div 
              className="profile-avatar"
              style={{
                background: `linear-gradient(135deg, ${user.profileColor || "#667eea"} 0%, ${adjustColorBrightness(user.profileColor || "#667eea", -20)} 100%)`
              }}
            >
              <span>{user.name.charAt(0).toUpperCase()}</span>
            </div>
            {!editingColor && (
              <button 
                className="profile-avatar-edit-btn"
                onClick={handleEditColor}
                title="Edit profile color"
                aria-label="Edit profile color"
              >
                <MdEdit />
              </button>
            )}
          </div>
          <div className="profile-info">
            <h3>{user.name}</h3>
            <p className="profile-email">{user.email}</p>
          </div>
        </div>
        
        {editingColor && (
          <div className="profile-color-picker">
            <div className="color-picker-header">
              <span>Choose a color</span>
              <div className="color-picker-actions">
                <button
                  className="color-picker-btn cancel"
                  onClick={handleCancelEdit}
                  disabled={updatingColor}
                >
                  <MdClose />
                </button>
              </div>
            </div>
            <div className="color-picker-grid">
              {PROFILE_COLORS.map((color) => (
                <button
                  key={color.value}
                  className={`color-option ${selectedColor === color.value ? 'selected' : ''}`}
                  style={{
                    background: `linear-gradient(135deg, ${color.value} 0%, ${adjustColorBrightness(color.value, -20)} 100%)`
                  }}
                  onClick={() => setSelectedColor(color.value)}
                  disabled={updatingColor}
                  title={color.name}
                >
                  {selectedColor === color.value && <MdCheck />}
                </button>
              ))}
            </div>
            <button
              className="color-picker-save-btn"
              onClick={() => handleColorUpdate(selectedColor)}
              disabled={updatingColor || selectedColor === (user?.profileColor || "#667eea")}
            >
              {updatingColor ? "Saving..." : "Save Color"}
            </button>
          </div>
        )}
      </div>

      <div className="profile-section">
        <h3 className="section-title">
          <MdTrendingUp />
          <span>Voting Stats</span>
        </h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Points Available Today</div>
              <div className="stat-value">{user.pointsLeftToday || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Total Votes Cast</div>
              <div className="stat-value">{user.totalVotes || 0}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

